import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  EmailAssociationsResponse,
  EmailErrorAssociationsResponse,
  EmailLinkDto,
  EmailLinkRequest,
  EmailLinkTarget,
} from '../email-app.interfaces';

/**
 * Service for managing email-client-application associations
 * Integrates with backend /emails endpoints
 */
@Injectable({
  providedIn: 'root',
})
export class EmailAppService {
  private http = inject(HttpClient);
  private readonly baseUrl = 'emails';

  /**
   * Build the request body for a link/unlink call, dropping empty ids so we never
   * send a target the backend would reject.
   */
  private buildRequest(
    target: EmailLinkTarget,
    payload: Omit<EmailLinkRequest, keyof EmailLinkTarget>
  ): EmailLinkRequest | null {
    const request: EmailLinkRequest = { ...payload };
    if (target.emailId) request.emailId = target.emailId;
    if (target.threadId) request.threadId = target.threadId;

    return request.emailId || request.threadId ? request : null;
  }

  private missingTarget(): Observable<never> {
    return throwError(() => new Error('emailId or threadId is required'));
  }

  /**
   * Link an email or a whole thread to a client
   * POST /emails/link-client
   */
  linkClient(target: EmailLinkTarget, clientId: number): Observable<EmailLinkDto> {
    const request = this.buildRequest(target, { clientId });
    if (!request) return this.missingTarget();

    return this.http.post<EmailLinkDto>(`${this.baseUrl}/link-client`, request).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  linkClientAndApplication(
    target: EmailLinkTarget,
    clientId: number,
    applicationId?: number
  ): Observable<EmailLinkDto> {
    // First link the client, then link the application if provided
    return this.linkClient(target, clientId).pipe(
      switchMap(clientLinkResponse => {
        if (applicationId) {
          return this.linkApplication(target, applicationId);
        } else {
          // Return the client link response if no applicationId is provided
          return of(clientLinkResponse);
        }
      }),
      map(response => {
        return response;
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  /**
   * Link an email or a whole thread to an application
   * PATCH /emails/link-application
   */
  linkApplication(target: EmailLinkTarget, applicationId: number): Observable<EmailLinkDto> {
    const request = this.buildRequest(target, { applicationId });
    if (!request) return this.missingTarget();

    return this.http.patch<EmailLinkDto>(`${this.baseUrl}/link-application`, request).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  /**
   * Unlink an email or a whole thread from a client (and its application)
   * DELETE /emails/unlink
   */
  unlinkClient(target: EmailLinkTarget, clientId: number): Observable<EmailLinkDto> {
    const body = this.buildRequest(target, { clientId });
    if (!body) return this.missingTarget();

    return this.http.delete<EmailLinkDto>(`${this.baseUrl}/unlink`, { body });
  }

  /**
   * Unlink an email or a whole thread from an application
   * DELETE /emails/unlink-application
   */
  unlinkApplication(
    target: EmailLinkTarget,
    clientId: number,
    applicationId: number
  ): Observable<EmailLinkDto> {
    const body = this.buildRequest(target, { clientId, applicationId });
    if (!body) return this.missingTarget();

    return this.http.delete<EmailLinkDto>(`${this.baseUrl}/unlink-application`, { body });
  }

  /**
   * Get associations for multiple emails and/or threads
   * GET /emails/associations?emailIds=m1&emailIds=m2&threadIds=t1
   * The response is a flat map keyed by the ids that were requested.
   */
  getAssociations(target: {
    emailIds?: string[];
    threadIds?: string[];
  }): Observable<EmailAssociationsResponse> {
    const params: { [param: string]: string[] } = {};
    if (target.emailIds?.length) params['emailIds'] = target.emailIds;
    if (target.threadIds?.length) params['threadIds'] = target.threadIds;

    // The endpoint returns 400 when called without any parameter
    if (Object.keys(params).length === 0) {
      return of({});
    }

    return this.http
      .get<EmailAssociationsResponse>(`${this.baseUrl}/associations`, { params })
      .pipe(
        map(response => {
          return response;
        }),
        catchError(error => {
          throw error;
        })
      );
  }

  /**
   * Get ingestion errors for multiple threads.
   * GET /emails/error-associations?threadIds=thread-1,thread-2
   * Returns a map keyed by threadId; each error carries its own `emailId`.
   */
  getErrorAssociations(threadIds: string[]): Observable<EmailErrorAssociationsResponse> {
    if (!threadIds || threadIds.length === 0) {
      return of({});
    }

    return this.http
      .get<EmailErrorAssociationsResponse>(`${this.baseUrl}/error-associations`, {
        params: {
          threadIds: threadIds.join(','),
        },
      })
      .pipe(
        map(response => {
          return response;
        }),
        catchError(error => {
          throw error;
        })
      );
  }

  /**
   * Get association for a single thread
   * Convenience method that calls getAssociations with a single thread ID
   */
  getThreadAssociation(threadId: string): Observable<EmailLinkDto | null> {
    return this.getAssociations({ threadIds: [threadId] }).pipe(
      map(response => {
        const associations = response[threadId];
        // Return the first association if exists, otherwise null
        return associations && associations.length > 0 ? associations[0] : null;
      })
    );
  }

  /**
   * Get client IDs by email addresses
   * GET /emails/client-ids?emails=email1@example.com,email2@example.com
   * Returns a map of email address to list of client IDs
   */
  getClientIdsByEmails(emails: string[]): Observable<{ [email: string]: number[] }> {
    if (!emails || emails.length === 0) {
      return of({});
    }

    return this.http
      .get<{ [email: string]: number[] }>(`${this.baseUrl}/client-ids`, {
        params: {
          emails: emails,
        },
      })
      .pipe(
        map(response => {
          return response;
        }),
        catchError(error => {
          throw error;
        })
      );
  }
}
